import { InfoCircleOutlined } from "@ant-design/icons";
import { Card, Flex, Tooltip } from "antd";
import { CSSProperties, ReactNode } from "react";
import { colors } from "shared/theme/color";
import { translateMessage } from "shared/utils";
import { textPx } from "shared/constants/zoom";

// The big display figure ("Rp 77.000.000", "17").
const numberValueStyle: CSSProperties = {
  fontSize: textPx(30),
  letterSpacing: "-0.02em",
  fontVariantNumeric: "tabular-nums",
};

// A name in place of a figure (a best-selling product): one step down the
// type scale (24, the AntD heading size below the 30 figures), at most two
// lines, then "…" with the full name on hover. At the figures' size, a long
// product name wrapped and made its card taller than the rest of the row.
const textValueStyle: CSSProperties = {
  fontSize: textPx(24),
  lineHeight: 1.3,
  letterSpacing: "-0.01em",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "anywhere",
};

// Design-system metric card (class-pages prototype): bg-soft icon chip, big
// display value, muted label, optional info tooltip (the same dark Tooltip
// every other hover explanation in the admin uses). Shared by the list-page
// analytics rows so the styling lives in one place.
//
// Every card fills its row's height and keeps its value and label at the
// bottom, so the labels of a row line up even when one value takes more room.
const MetricCard = ({
  icon,
  title,
  value,
  information,
  text = false,
}: {
  icon: ReactNode;
  title: string;
  value: string | number;
  information?: string;
  // The value is a name, not a figure.
  text?: boolean;
}) => {
  const shown = translateMessage(String(value));
  return (
    <Card
      style={{
        flex: 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      styles={{
        body: {
          padding: 22,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: colors.bgSoft,
          color: colors.ink2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: textPx(16),
        }}
      >
        {icon}
      </div>
      <div style={{ marginTop: "auto", paddingTop: 12 }}>
        <div
          title={text ? shown : undefined}
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 600,
            ...(text ? textValueStyle : numberValueStyle),
          }}
        >
          {shown}
        </div>
        {/* The info icon sits beside the caption, like the Beranda cards, not
            in the card's top-right corner: the right-most card's corner is a
            few px from the viewport edge, where the tooltip could never
            centre on the icon and was pushed flush against the window. */}
        <Flex align="center" gap={5} style={{ marginTop: 2 }}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {translateMessage(title)}
          </span>
          {information && (
            <Tooltip title={translateMessage(information)}>
              <InfoCircleOutlined
                style={{ fontSize: textPx(12), color: colors.ink3, cursor: "help", flexShrink: 0 }}
              />
            </Tooltip>
          )}
        </Flex>
      </div>
    </Card>
  );
};

export default MetricCard;
